import React from "react";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { payInvoice } from "../../services/newInvoicesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatMoney } from "./classUtils";

interface PackagePaymentPromptDialogProps {
  open: boolean;
  invoiceId: string | number | null;
  amount: number;
  onClose: () => void;
  /** Fired after the invoice is successfully paid in full. */
  onPaid: () => void;
}

/**
 * Shown right after a package purchase/assignment — the client_package it
 * just created is UNPAID until this invoice is settled (partially or fully),
 * and an unpaid package silently can't be redeemed for a class booking. This
 * skips the "go find the invoice and mark it paid" detour by firing the same
 * full-payment call the Invoices page uses, right from here.
 */
const PackagePaymentPromptDialog: React.FC<PackagePaymentPromptDialogProps> = ({
  open,
  invoiceId,
  amount,
  onClose,
  onPaid,
}) => {
  const [paying, setPaying] = React.useState(false);

  const handlePayFull = async () => {
    if (!invoiceId) return;
    setPaying(true);
    try {
      await payInvoice(String(invoiceId), {
        amount,
        method: "CASH",
        provider: "MANUAL",
        status: "SUCCESS",
      });
      toast.success("Invoice paid in full — the package is now active.");
      onPaid();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to record the payment."));
    } finally {
      setPaying(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!paying) onClose();
      }}
      title="Collect payment now?"
      actions={
        <>
          <CustomButton
            label="Pay Later"
            variant="outlined"
            onClick={onClose}
            disabled={paying}
          />
          <CustomButton
            label={`Pay Full Amount — ${formatMoney(amount)}`}
            variant="contained"
            bgColor="#2e7d32"
            textColor="#fff"
            loading={paying}
            disabled={paying}
            onClick={handlePayFull}
          />
        </>
      }
    >
      <Typography sx={{ fontSize: 14 }}>
        The package stays inactive until this invoice is paid — fully or
        partially. If they're paying in full right now, do it here to skip the
        invoice screen; otherwise pay later or record a partial payment from
        Invoices.
      </Typography>
    </CustomDialog>
  );
};

export default PackagePaymentPromptDialog;
